import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  BadgeIndianRupee,
  PackageCheck,
  ShoppingBag,
  UserCircle2,
  Menu,
  X,
  LogOut,
  ChevronRight,
  Home,
  Download,
  Wand2,
  Truck,
  CreditCard,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { useAuth } from "../../lib/auth-context";
import { useSubscription } from "../../lib/subscription-context";
import { PANEL_MENU } from "../../lib/panel-data";
import { LOGO_URL } from "../../lib/landing-data";

const ICON_MAP = {
  LayoutDashboard,
  Users,
  BadgeIndianRupee,
  PackageCheck,
  ShoppingBag,
  UserCircle2,
  Wand2,
  Truck,
  CreditCard,
};

const SidebarLinks = ({ onNavigate, menu }) => (
  <nav className="flex flex-col gap-0.5 p-3" data-testid="panel-sidebar-nav">
    {menu.map((item) => {
      const Icon = ICON_MAP[item.icon] || LayoutDashboard;
      return (
        <NavLink
          key={item.id}
          to={item.to}
          end
          onClick={onNavigate}
          data-testid={`panel-nav-${item.id}`}
          className={({ isActive }) =>
            `relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
              isActive
                ? "bg-purple-50 text-purple-700 shadow-sm"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            }`
          }
        >
          {({ isActive }) => (
            <>
              {isActive && (
                <motion.span
                  layoutId="panel-active-pill"
                  className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full bg-gradient-to-b from-purple-600 to-violet-600"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              <Icon className="w-4.5 h-4.5 shrink-0" strokeWidth={2.2} />
              <span className="flex-1">{item.label}</span>
              {isActive && <ChevronRight className="w-3.5 h-3.5 opacity-60" />}
            </>
          )}
        </NavLink>
      );
    })}
  </nav>
);

export const PanelLayout = () => {
  const { user, logout } = useAuth();
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
    navigate("/");
  };

  const downloadExtension = () => {
    window.open("https://chromewebstore.google.com/detail/easylister/ehjfpifffjcdcaammjaichihpdgncoif", "_blank", "noopener,noreferrer");
  };

  // Plain "User" accounts (userType 3) don't manage sub-users.
  const visibleMenu = user?.userType === 3
    ? PANEL_MENU.filter((m) => m.id !== "users" && m.id !== "dashboard")
    : PANEL_MENU;
  const activeMenu = PANEL_MENU.find((m) => location.pathname.startsWith(m.to));

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50/40 via-white to-fuchsia-50/30">
      {/* Sidebar — desktop */}
      <aside
        data-testid="panel-sidebar"
        className="hidden lg:flex fixed inset-y-0 left-0 w-64 z-40 flex-col bg-white border-r border-slate-200"
      >
        <Link to="/" className="flex items-center h-20 px-5 border-b border-slate-100 shrink-0">
          <img src={LOGO_URL} alt="EasyLister" className="h-10 w-auto object-contain" />
        </Link>

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Workspace
          </div>
          <SidebarLinks menu={visibleMenu} />
        </div>

        <div className="p-3 border-t border-slate-100 space-y-2">
          <Link
            to="/"
            data-testid="panel-back-to-site"
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-slate-500 hover:bg-slate-50 hover:text-purple-700"
          >
            <Home className="w-4 h-4" /> Back to website
          </Link>
          <button
            onClick={handleLogout}
            data-testid="panel-logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="lg:hidden fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              className="lg:hidden fixed inset-y-0 left-0 w-72 z-50 bg-white border-r border-slate-200 flex flex-col"
              data-testid="panel-sidebar-mobile"
            >
              <div className="flex items-center justify-between h-20 px-5 border-b border-slate-100">
                <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center">
                  <img src={LOGO_URL} alt="EasyLister" className="h-10 w-auto object-contain" />
                </Link>
                <button onClick={() => setMobileOpen(false)} className="p-2 rounded-lg hover:bg-slate-100">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <SidebarLinks menu={visibleMenu} onNavigate={() => setMobileOpen(false)} />
              </div>
              <div className="p-3 border-t border-slate-100 space-y-2">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-slate-500 hover:bg-slate-50"
                >
                  <Home className="w-4 h-4" /> Back to website
                </Link>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleLogout();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        <header
          data-testid="panel-topbar"
          className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200"
        >
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-10 h-16 sm:h-20">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                data-testid="panel-mobile-menu"
                aria-label="open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <div className="font-heading text-base sm:text-xl font-extrabold text-slate-900 leading-none truncate">
                  {activeMenu?.label || "Dashboard"}
                </div>
                <div className="text-[11px] sm:text-xs text-slate-500 mt-1 truncate">
                  Hi {user?.name?.split(" ")[0] || "Seller"}, welcome back
                  {user?.sellerName ? ` to ${user.sellerName}` : ""}.
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <div
                data-testid="panel-plan-pill"
                className="flex items-center gap-2 px-3 sm:px-3.5 h-9 sm:h-10 rounded-full bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-md shadow-purple-600/25"
              >
                <CreditCard className="w-4 h-4" />
                <span className="text-xs font-bold capitalize">
                  {subscription?.plan ?? "No plan"}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={downloadExtension}
                data-testid="panel-download-extension"
                className="hidden sm:inline-flex rounded-full border-purple-200 text-purple-700 hover:bg-purple-50 font-semibold h-10 px-4"
              >
                <Download className="w-4 h-4 mr-1.5" /> Get Extension
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/app/subscription")}
                data-testid="panel-top-upgrade"
                className="hidden sm:inline-flex rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold h-10 px-4"
              >
                <Zap className="w-4 h-4 mr-1.5" /> Upgrade
              </Button>
              <div
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-purple-600 to-violet-700 text-white flex items-center justify-center font-bold text-sm shrink-0"
                title={user?.email}
              >
                {(user?.name?.[0] || "S").toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 sm:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
